import React, { useRef, useState, useEffect } from 'react';
import { Camera as CameraIcon, X } from 'lucide-react';

interface CameraProps {
  onCapture: (imageDataUrl: string) => void;
  onClose: () => void;
}

function isIOS() {
  const ua = navigator.userAgent;
  const iOSUA = /iPad|iPhone|iPod/.test(ua);
  const iPadOS13Plus = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOSUA || iPadOS13Plus;
}

function isStandalonePWA() {
  // iOS: navigator.standalone; cross-platform: display-mode: standalone
  return (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
         (typeof (navigator as any).standalone !== 'undefined' && (navigator as any).standalone === true);
}

export const Camera: React.FC<CameraProps> = ({ onCapture, onClose }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string>('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Hide video until camera ready

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const ensureVideoReady = async (video: HTMLVideoElement) => {
    // Make sure attributes are present before srcObject assignment
    video.setAttribute('playsinline', '');
    (video as any).setAttribute('webkit-playsinline', '');
    video.muted = true;

    // Wait for canplay so width/height are set
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      await new Promise<void>((resolve) => {
        const onCanPlay = () => {
          video.removeEventListener('canplay', onCanPlay);
          resolve();
        };
        video.addEventListener('canplay', onCanPlay);
      });
    }

    // A small rAF helps iOS actually paint a frame
    await new Promise(requestAnimationFrame);

    try {
      await video.play();
    } catch (e) {
      // If autoplay is blocked, we'll depend on user gesture
      console.warn('video.play() blocked or failed: ', e);
    }

    // Wait until we truly have frame data
    if (video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA) {
      await new Promise<void>((resolve) => {
        const onTimeUpdate = () => {
          if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
            video.removeEventListener('timeupdate', onTimeUpdate);
            resolve();
          }
        };
        video.addEventListener('timeupdate', onTimeUpdate);
      });
    }
  };

  const startCamera = async () => {
    try {
      // Check if camera permissions are available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError('Camera not available on this device.');
        return;
      }

      const onIOSPWA = isIOS() && isStandalonePWA();

      // Simple constraints on iOS PWAs; avoid ideal width/height
      const constraints: MediaStreamConstraints = onIOSPWA
        ? { video: { facingMode: { ideal: 'environment' } }, audio: false }
        : {
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              // @ts-ignore - focusMode is valid but not in TS types
              focusMode: { ideal: 'continuous' },
            },
            audio: false,
          };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoRef.current) {
        const video = videoRef.current;

        // Set attributes before srcObject
        video.setAttribute('playsinline', '');
        (video as any).setAttribute('webkit-playsinline', '');
        video.muted = true;
        video.srcObject = mediaStream;

        await ensureVideoReady(video);
      }

      setStream(mediaStream);
      setError('');
      setIsLoading(false); // Camera is now ready, show the video
    } catch (err: any) {
      console.error('Camera error:', err);

      // Provide specific error messages based on error type
      const isAndroid = /Android/i.test(navigator.userAgent);

      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        if (isAndroid) {
          setError('Camera permission denied. Please grant camera permission in your device settings, then try again.');
        } else {
          setError('Camera access denied. To fix: Go to iOS Settings → Safari → Camera → Allow, then refresh this page.');
        }
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setError('Camera is being used by another app. Close other apps and try again.');
      } else {
        setError('Unable to access camera. Please check permissions and reload.');
      }
      setIsLoading(false); // Stop loading on error too
    }
  };

  const stopCamera = async () => {
    if (stream) {
      stream.getTracks().forEach((t) => {
        t.stop();
        t.enabled = false;
      });
      await new Promise((r) => setTimeout(r, 80));
      setStream(null);
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      // Force detach
      try { videoRef.current.load(); } catch {}
    }
  };

  const captureViaImageCapture = async (video: HTMLVideoElement) => {
    const track = video.srcObject instanceof MediaStream
      ? video.srcObject.getVideoTracks()[0]
      : null;
    if (!track) return null;

    // @ts-ignore - ImageCapture may not be in TS lib
    if (typeof window.ImageCapture === 'function') {
      try {
        // @ts-ignore - ImageCapture.grabFrame not in TS lib
        const ic = new window.ImageCapture(track);
        // @ts-ignore
        const bitmap = await ic.grabFrame(); // modern Safari supports this
        return bitmap;
      } catch (e) {
        console.warn('ImageCapture.grabFrame failed, falling back:', e);
      }
    }
    return null;
  };

  const capturePhoto = async () => {
    if (isCapturing) return;
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;

    try {
      // Ensure we truly have a frame
      await ensureVideoReady(video);

      // Try ImageCapture first (prevents black frames on iOS PWAs)
      let bitmap: ImageBitmap | null = await captureViaImageCapture(video);

      let width: number;
      let height: number;

      if (bitmap) {
        width = bitmap.width;
        height = bitmap.height;
      } else {
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          throw new Error('Camera not ready (zero dimensions).');
        }
        width = video.videoWidth;
        height = video.videoHeight;
      }

      // Resize bounds - 1200px is plenty for OCR on 4x6" tickets
      const MAX_W = 1200;
      const MAX_H = 1200;
      if (width > height && width > MAX_W) {
        height = Math.round((height * MAX_W) / width);
        width = MAX_W;
      } else if (height >= width && height > MAX_H) {
        width = Math.round((width * MAX_H) / height);
        height = MAX_H;
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context unavailable');

      if (bitmap) {
        ctx.drawImage(bitmap, 0, 0, width, height);
      } else {
        // Give iOS a tiny paint window
        await new Promise(requestAnimationFrame);
        ctx.drawImage(video, 0, 0, width, height);
      }

      const imageDataUrl = canvas.toDataURL('image/jpeg', 0.75);

      // Clean up
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      canvas.width = 0;
      canvas.height = 0;

      // Optionally stop camera after capture (your original behavior)
      await stopCamera();

      onCapture(imageDataUrl);
    } catch (err) {
      console.error('Error capturing photo:', err);
      setError('Failed to capture photo. If you are on iPad PWA, tap "Start Camera" first and try again.');
      // Keep stream alive so user can retry
    } finally {
      setIsCapturing(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: '#000',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        padding: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)'
      }}>
        <h3 style={{ color: 'white', margin: 0 }}>Take Photo</h3>
        <button
          onClick={handleClose}
          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: '8px' }}
        >
          <X size={24} />
        </button>
      </div>

      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', backgroundColor: '#000' }}>
        {/* Always render video so ref is available, but hide until ready */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: isLoading || error ? 0 : 1,
            transition: 'opacity 0.2s ease-in-out'
          }}
        />

        {/* Loading overlay */}
        {isLoading && !error && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'white'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid rgba(255,255,255,0.2)',
              borderTopColor: '#22c55e',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Starting camera...</div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Error overlay */}
        {error && (
          <div style={{
            color: 'white',
            padding: '32px',
            textAlign: 'center',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '400px',
            backgroundColor: 'rgba(239, 68, 68, 0.15)',
            borderRadius: '12px',
            border: '2px solid #ef4444',
            lineHeight: '1.6'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Camera Error</div>
            <div style={{ fontSize: '14px' }}>{error}</div>
          </div>
        )}
      </div>

      <div style={{
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        backgroundColor: 'rgba(0,0,0,0.5)'
      }}>
        <button
          onClick={capturePhoto}
          disabled={!!error || isCapturing}
          style={{
            width: '70px',
            height: '70px',
            borderRadius: '50%',
            backgroundColor: 'white',
            border: '4px solid #22c55e',
            cursor: (!!error || isCapturing) ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: (!!error || isCapturing) ? 0.5 : 1
          }}
        >
          <CameraIcon size={32} color="#22c55e" />
        </button>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};
